import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import classnames from 'classnames'
import { deleteTariffRateApi } from '../../../apis/apis'
import type { TariffRateItem, TariffStatus } from '../../../apis/types'
import CopyableValue from '../../../components/CopyableValue'
import Modal from '../../../components/Modal'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { formatUTCDetailedDate } from '../../../util/string'
import { CountBadge, FieldTooltip, RATE_GROUP_GLOSSARY, SortableColumnHeader } from './TariffFieldKey'
import { RateEnumBadge, TARIFF_TABLE_ROW_HOVER } from './TariffChips'
import {
    compareRateRows,
    nextSortState,
    rateTableValue,
    type RateTableSortKey,
    type SortState,
} from './tariffRateSort'
import { CreateTariffRateForm } from './CreateTariffRateForm'
import { EditTariffRateModal } from './EditTariffRateModal'
import { isEmptyRateWindow, isOpenRateEnd } from './tariffHistoryUtils'

/** Prefer the billing-important groups first when choosing a default-open section. */
export const RATE_GROUP_PRIORITY = [
    'GENERATION',
    'DELIVERY',
    'DEMAND',
    'NBC',
    'BUNDLED_FEE',
    'PDP',
] as const

export function defaultExpandedGroup(groups: string[]): string | null {
    for (const preferred of RATE_GROUP_PRIORITY) {
        if (groups.includes(preferred)) return preferred
    }
    return groups[0] ?? null
}

const SEARCH_MARK_CLASS = 'rounded-sm bg-amber-200 px-0.5 text-amber-950 dark:bg-amber-500/50 dark:text-amber-50'

/** Case-insensitive substring highlight. Literal match (search may include `.` in a rate). */
export function highlightRateSearch(text: string, term: string): React.ReactNode {
    const query = term.trim()
    if (query === '' || text === '') return text
    const lowerText = text.toLowerCase()
    const lowerQuery = query.toLowerCase()
    if (!lowerText.includes(lowerQuery)) return text

    const nodes: React.ReactNode[] = []
    let cursor = 0
    let matchAt = lowerText.indexOf(lowerQuery)
    let key = 0
    while (matchAt !== -1) {
        if (matchAt > cursor) nodes.push(text.slice(cursor, matchAt))
        nodes.push(
            <mark key={key} className={SEARCH_MARK_CLASS}>
                {text.slice(matchAt, matchAt + query.length)}
            </mark>
        )
        key += 1
        cursor = matchAt + query.length
        matchAt = lowerText.indexOf(lowerQuery, cursor)
    }
    if (cursor < text.length) nodes.push(text.slice(cursor))
    return nodes
}

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/** Matches backend RATE_GROUP_ORDER for section display. */
const RATE_GROUP_ORDER: Record<string, number> = {
    SERVICE: 0,
    DEMAND: 1,
    PDP: 2,
    GENERATION: 3,
    DELIVERY: 4,
    NBC: 5,
    BUNDLED_FEE: 6,
    NONE: 7,
}

export function isActiveRate(rate: TariffRateItem, now = new Date()): boolean {
    if (isEmptyRateWindow(rate.rateEffectiveDate, rate.rateEndDate)) return false
    if (rate.rateEndDate == null || isOpenRateEnd(rate.rateEndDate)) return true
    const endDay = new Date(rate.rateEndDate).toISOString().slice(0, 10)
    const nowDay = now.toISOString().slice(0, 10)
    return endDay >= nowDay
}

function formatRateEndLabel(rate: TariffRateItem): string | null {
    if (isEmptyRateWindow(rate.rateEffectiveDate, rate.rateEndDate)) return 'Set on activation'
    if (isOpenRateEnd(rate.rateEndDate)) return 'Open'
    if (rate.rateEndDate == null) return 'Open'
    return formatUTCDetailedDate(new Date(rate.rateEndDate))
}

const thClass =
    'px-2 py-1.5 text-left text-xs font-semibold tracking-wide text-slate-600 dark:text-slate-300'
const tdClass = `px-2 py-1 text-xs text-slate-700 dark:text-slate-200 ${TARIFF_TABLE_ROW_HOVER}`
const notesColClass = 'min-w-[16rem] whitespace-normal'
const nameColClass = 'min-w-[14rem] max-w-[22rem]'
const stickyHeadBg = 'bg-slate-50 dark:bg-slate-900/60'
const stickyNameShadow =
    'shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)] dark:shadow-[2px_0_4px_-2px_rgba(0,0,0,0.35)]'
const stickyActionsShadow =
    'shadow-[-2px_0_4px_-2px_rgba(15,23,42,0.08)] dark:shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.35)]'

function stickyNameCellClass(active: boolean): string {
    return classnames(
        tdClass,
        'sticky left-0 z-[1]',
        nameColClass,
        stickyNameShadow,
        active ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/80 dark:bg-slate-900/40'
    )
}

function stickyActionsCellClass(active: boolean): string {
    return classnames(
        tdClass,
        'sticky right-0 z-[1] text-right',
        stickyActionsShadow,
        active ? 'bg-white dark:bg-slate-800' : 'bg-slate-50/80 dark:bg-slate-900/40'
    )
}

export function useTariffRateFilters(): {
    rateSearch: string
    hasActiveFilters: boolean
    setRateParam: (key: string, value: string | null) => void
    clearFilters: () => void
} {
    const [searchParams, setSearchParams] = useSearchParams()

    const rateSearch = searchParams.get('rateSearch') ?? ''
    const hasActiveFilters = rateSearch.trim() !== ''

    const setRateParam = useCallback(
        (key: string, value: string | null): void => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                if (value != null && value !== '') next.set(key, value)
                else next.delete(key)
                next.delete('ratePage')
                return next
            })
        },
        [setSearchParams]
    )

    const clearFilters = useCallback((): void => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.delete('rateSearch')
            next.delete('activeOnly')
            next.delete('rateColumns')
            next.delete('ratePage')
            return next
        })
    }, [setSearchParams])

    return { rateSearch, hasActiveFilters, setRateParam, clearFilters }
}

export const TariffRatesTable: React.FC<{
    rates: TariffRateItem[]
    canEdit: boolean
    tariffCuid?: string
    tariffHistoryCuid?: string | null
    historyEffectiveDate?: string | null
    historyEndDate?: string | null
    historyStatus?: TariffStatus
    onRateUpdated?: (rate: TariffRateItem) => void
    onRateCreated?: (rate: TariffRateItem) => void
    onRateDeleted?: (rateCuid: string) => void
}> = ({
    rates,
    canEdit,
    tariffCuid,
    tariffHistoryCuid,
    historyEffectiveDate,
    historyEndDate,
    historyStatus,
    onRateUpdated,
    onRateCreated,
    onRateDeleted,
}) => {
    const dispatch = useAppDispatch()
    const { rateSearch, setRateParam } = useTariffRateFilters()
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
    const [editingRate, setEditingRate] = useState<TariffRateItem | null>(null)
    const [menuOpenCuid, setMenuOpenCuid] = useState<string | null>(null)
    const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null)
    const [confirmModal, setConfirmModal] = useState<React.ReactElement | null>(null)
    const [deletingCuid, setDeletingCuid] = useState<string | null>(null)
    const [sort, setSort] = useState<SortState<RateTableSortKey> | null>(null)

    const rateGroups = useMemo(
        () => [...new Set(rates.map((r) => r.rateGroup))].sort(),
        [rates]
    )

    const inactiveCount = useMemo(() => rates.filter((r) => !isActiveRate(r)).length, [rates])

    const filtered = useMemo(() => {
        const term = rateSearch.trim().toLowerCase()
        if (term === '') return rates
        return rates.filter((r) => {
            const haystack = [
                r.cuid,
                r.name,
                r.season,
                r.touPeriod,
                r.rateGroup,
                r.category,
                r.type,
                String(r.rate),
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(term)
        })
    }, [rates, rateSearch])

    const grouped = useMemo(() => {
        const map = new Map<string, TariffRateItem[]>()
        for (const rate of filtered) {
            const list = map.get(rate.rateGroup) ?? []
            list.push(rate)
            map.set(rate.rateGroup, list)
        }
        const entries = [...map.entries()].map(([group, list]) => {
            const ordered = [...list].sort((a, b) => compareRateRows(a, b, sort, rateTableValue))
            return [group, ordered] as const
        })
        return entries.sort(([a], [b]) => {
            if (sort?.key === 'rateGroup') {
                const diff = a.localeCompare(b)
                return sort.dir === 'ASC' ? diff : -diff
            }
            return (RATE_GROUP_ORDER[a] ?? 99) - (RATE_GROUP_ORDER[b] ?? 99)
        })
    }, [filtered, sort])

    const visibleGroups = useMemo(() => grouped.map(([g]) => g), [grouped])

    const isGroupExpanded = (group: string): boolean => !collapsedGroups.has(group)

    const toggleGroup = (group: string): void => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(group)) next.delete(group)
            else next.add(group)
            return next
        })
    }

    useEffect(() => {
        if (rateSearch.trim() !== '') {
            setCollapsedGroups(new Set())
            return
        }
        const keepOpen = defaultExpandedGroup(rateGroups)
        setCollapsedGroups(new Set(rateGroups.filter((g) => g !== keepOpen)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateGroups.join('|'), rateSearch])

    const closeRateMenu = (): void => {
        setMenuOpenCuid(null)
        setMenuPos(null)
    }

    useEffect(() => {
        if (menuOpenCuid == null) return
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                setMenuOpenCuid(null)
                setMenuPos(null)
            }
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [menuOpenCuid])

    const handleSort = (key: string): void => {
        setSort((prev) => nextSortState(prev, key as RateTableSortKey))
    }

    const handleRateSaved = (updated: TariffRateItem): void => {
        onRateUpdated?.(updated)
        void dispatch(pushToast({ type: 'success', message: 'Rate updated' }))
    }

    const handleDeleteRate = async (rate: TariffRateItem): Promise<void> => {
        closeRateMenu()
        let reason = ''
        try {
            const confirmed = await new Promise<unknown>((resolve, reject) => {
                setConfirmModal(
                    <Modal
                        resolve={resolve}
                        reject={reject}
                        title="Delete this rate?"
                        message={`Remove "${rate.name}" from this rateset. This cannot be undone.`}
                        reasonRequired
                    />
                )
            })
            reason = typeof confirmed === 'string' ? confirmed.trim() : ''
        } catch {
            setConfirmModal(null)
            return
        }
        setConfirmModal(null)
        if (reason === '') return
        setDeletingCuid(rate.cuid)
        try {
            await deleteTariffRateApi(rate.cuid, reason)
            onRateDeleted?.(rate.cuid)
            void dispatch(pushToast({ type: 'success', message: 'Rate deleted' }))
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to delete rate' }))
        } finally {
            setDeletingCuid(null)
        }
    }

    const createForm =
        canEdit && tariffCuid != null && tariffHistoryCuid != null ? (
            <CreateTariffRateForm
                tariffCuid={tariffCuid}
                tariffHistoryCuid={tariffHistoryCuid}
                historyEffectiveDate={historyEffectiveDate}
                historyEndDate={historyEndDate}
                historyStatus={historyStatus}
                siblingEndDates={rates.map((rate) => rate.rateEndDate)}
                onCreated={(rate) => onRateCreated?.(rate)}
            />
        ) : null

    if (rates.length === 0) {
        return (
            <div className="space-y-3 p-4">
                {createForm}
                <div className="text-sm text-slate-500 dark:text-slate-400">
                    No rates for the selected rateset.
                </div>
            </div>
        )
    }

    const colSpan = 12 + (canEdit ? 1 : 0)

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[12rem] flex-1">
                    <label
                        htmlFor="tariff-rate-search"
                        className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300"
                    >
                        Search rates
                    </label>
                    <input
                        id="tariff-rate-search"
                        type="search"
                        value={rateSearch}
                        onChange={(e) => setRateParam('rateSearch', e.target.value || null)}
                        placeholder="Name, rate, cuid, season, TOU, group…"
                        className={inputClass}
                    />
                </div>
                {createForm != null ? <div className="ml-auto pb-0.5">{createForm}</div> : null}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
                <span>
                    {rateSearch.trim() !== ''
                        ? `Showing ${filtered.length.toLocaleString()} of ${rates.length.toLocaleString()} rates`
                        : `${rates.length.toLocaleString()} rates`}
                    {inactiveCount > 0 ? ` · ${inactiveCount.toLocaleString()} inactive` : ''}
                </span>
                {visibleGroups.length > 0 && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setCollapsedGroups(new Set())}
                            className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                        >
                            Expand all
                        </button>
                        <span aria-hidden="true">·</span>
                        <button
                            type="button"
                            onClick={() => setCollapsedGroups(new Set(visibleGroups))}
                            className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                        >
                            Collapse all
                        </button>
                    </div>
                )}
            </div>

            {filtered.length === 0 ? (
                <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
                    No rates match this search.
                </div>
            ) : (
                    <div className="relative isolate overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
                        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                            <thead className="bg-slate-50 dark:bg-slate-900/60">
                                <tr>
                                    <SortableColumnHeader
                                        label="Name"
                                        sortKey="name"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={classnames(
                                            thClass,
                                            'sticky left-0 z-[2]',
                                            nameColClass,
                                            stickyHeadBg,
                                            stickyNameShadow
                                        )}
                                    />
                                    <SortableColumnHeader
                                        label="Rate"
                                        sortKey="rate"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={`${thClass} text-right`}
                                    />
                                    <SortableColumnHeader
                                        label="Type"
                                        sortKey="type"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="Category"
                                        sortKey="category"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="Season"
                                        sortKey="season"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="TOU"
                                        sortKey="touPeriod"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="Group"
                                        sortKey="rateGroup"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="Effective"
                                        sortKey="rateEffectiveDate"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="End"
                                        sortKey="rateEndDate"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="PCIA year"
                                        sortKey="vintageYear"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    <SortableColumnHeader
                                        label="Notes"
                                        sortKey="modifiedDescription"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={`${thClass} ${notesColClass}`}
                                    />
                                    <SortableColumnHeader
                                        label="Rate cuid"
                                        sortKey="cuid"
                                        activeKey={sort?.key ?? null}
                                        dir={sort?.dir ?? null}
                                        onSort={handleSort}
                                        className={thClass}
                                    />
                                    {canEdit ? (
                                        <th
                                            className={classnames(
                                                thClass,
                                                'sticky right-0 z-[2] text-right',
                                                stickyHeadBg,
                                                stickyActionsShadow
                                            )}
                                        >
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {grouped.map(([group, groupRates]) => {
                                    const expanded = isGroupExpanded(group)
                                    const groupHint = RATE_GROUP_GLOSSARY[group]
                                    return (
                                        <React.Fragment key={group}>
                                            <tr className="bg-slate-200/90 dark:bg-slate-700/90">
                                                <td colSpan={colSpan} className="p-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleGroup(group)}
                                                        aria-expanded={expanded}
                                                        className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs font-semibold tracking-wide text-slate-800 hover:bg-slate-300/70 dark:text-slate-100 dark:hover:bg-slate-600/70"
                                                    >
                                                        {expanded ? (
                                                            <ChevronDownIcon className="h-4 w-4 shrink-0" />
                                                        ) : (
                                                            <ChevronRightIcon className="h-4 w-4 shrink-0" />
                                                        )}
                                                        {groupHint != null ? (
                                                            <FieldTooltip content={groupHint}>
                                                                <span>{highlightRateSearch(group, rateSearch)}</span>
                                                            </FieldTooltip>
                                                        ) : (
                                                            <span>{highlightRateSearch(group, rateSearch)}</span>
                                                        )}
                                                        <CountBadge count={groupRates.length} tone="slate" />
                                                    </button>
                                                </td>
                                            </tr>
                                            {expanded &&
                                                groupRates.map((rate) => {
                                                    const active = isActiveRate(rate)
                                                    return (
                                                        <tr
                                                            key={rate.cuid}
                                                            className={classnames(
                                                                'group',
                                                                menuOpenCuid === rate.cuid && 'relative z-10',
                                                                !active &&
                                                                    'bg-slate-50/80 text-slate-400 opacity-70 dark:bg-slate-900/40'
                                                            )}
                                                        >
                                                            <td className={stickyNameCellClass(active)}>
                                                                {highlightRateSearch(rate.name, rateSearch)}
                                                            </td>
                                                            <td
                                                                className={`${tdClass} text-right font-mono tabular-nums`}
                                                            >
                                                                {highlightRateSearch(
                                                                    rate.rate.toFixed(6),
                                                                    rateSearch
                                                                )}
                                                            </td>
                                                            <td className={tdClass}>
                                                                <RateEnumBadge kind="type" label={rate.type}>
                                                                    {highlightRateSearch(rate.type, rateSearch)}
                                                                </RateEnumBadge>
                                                            </td>
                                                            <td className={tdClass}>
                                                                <RateEnumBadge kind="category" label={rate.category}>
                                                                    {highlightRateSearch(rate.category, rateSearch)}
                                                                </RateEnumBadge>
                                                            </td>
                                                            <td className={tdClass}>
                                                                <RateEnumBadge kind="season" label={rate.season}>
                                                                    {highlightRateSearch(
                                                                        rate.season ?? '',
                                                                        rateSearch
                                                                    )}
                                                                </RateEnumBadge>
                                                            </td>
                                                            <td className={tdClass}>
                                                                <RateEnumBadge kind="tou" label={rate.touPeriod}>
                                                                    {highlightRateSearch(
                                                                        rate.touPeriod ?? '',
                                                                        rateSearch
                                                                    )}
                                                                </RateEnumBadge>
                                                            </td>
                                                            <td className={tdClass}>
                                                                <RateEnumBadge
                                                                    kind="group"
                                                                    label={rate.rateGroup}
                                                                    title={RATE_GROUP_GLOSSARY[rate.rateGroup]}
                                                                >
                                                                    {highlightRateSearch(
                                                                        rate.rateGroup,
                                                                        rateSearch
                                                                    )}
                                                                </RateEnumBadge>
                                                            </td>
                                                            <td className={`${tdClass} whitespace-nowrap`}>
                                                                {formatUTCDetailedDate(
                                                                    new Date(rate.rateEffectiveDate)
                                                                )}
                                                            </td>
                                                            <td className={`${tdClass} whitespace-nowrap`}>
                                                                {formatRateEndLabel(rate)}
                                                            </td>
                                                            <td className={tdClass}>
                                                                <span className="font-mono tabular-nums">
                                                                    {rate.vintageYear ?? null}
                                                                </span>
                                                            </td>
                                                            <td className={`${tdClass} ${notesColClass}`}>
                                                                {rate.modifiedDescription ?? null}
                                                            </td>
                                                            <td className={tdClass}>
                                                                <CopyableValue
                                                                    value={rate.cuid}
                                                                    label="rate cuid"
                                                                    truncate={false}
                                                                    valueClassName="text-xs"
                                                                >
                                                                    {highlightRateSearch(rate.cuid, rateSearch)}
                                                                </CopyableValue>
                                                            </td>
                                                            {canEdit ? (
                                                                <td className={stickyActionsCellClass(active)}>
                                                                    <button
                                                                        type="button"
                                                                        className="rounded p-1 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                                                                        aria-label={`Actions for ${rate.name}`}
                                                                        aria-expanded={menuOpenCuid === rate.cuid}
                                                                        aria-haspopup="menu"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            if (menuOpenCuid === rate.cuid) {
                                                                                closeRateMenu()
                                                                                return
                                                                            }
                                                                            const rect =
                                                                                e.currentTarget.getBoundingClientRect()
                                                                            setMenuPos({
                                                                                top: rect.bottom + 4,
                                                                                right:
                                                                                    window.innerWidth - rect.right,
                                                                            })
                                                                            setMenuOpenCuid(rate.cuid)
                                                                        }}
                                                                    >
                                                                        <EllipsisVerticalIcon className="h-4 w-4" />
                                                                    </button>
                                                                </td>
                                                            ) : null}
                                                        </tr>
                                                    )
                                                })}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
            )}
            <EditTariffRateModal
                rate={editingRate}
                open={editingRate != null}
                onClose={() => setEditingRate(null)}
                onSaved={handleRateSaved}
                historyEffectiveDate={historyEffectiveDate}
                historyEndDate={historyEndDate}
                historyStatus={historyStatus}
                siblingEndDates={rates.map((rate) => rate.rateEndDate)}
            />
            {confirmModal}
            {menuOpenCuid != null && menuPos != null
                ? createPortal(
                      <div>
                          <div
                              className="fixed inset-0 z-[9998]"
                              aria-hidden
                              onClick={closeRateMenu}
                          />
                          <div
                              role="menu"
                              aria-label="Rate actions"
                              className="fixed z-[9999] w-36 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                              style={{ top: menuPos.top, right: menuPos.right }}
                          >
                              <button
                                  type="button"
                                  role="menuitem"
                                  className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                                  onClick={() => {
                                      const rate = rates.find((r) => r.cuid === menuOpenCuid)
                                      closeRateMenu()
                                      if (rate != null) setEditingRate(rate)
                                  }}
                              >
                                  Edit rate
                              </button>
                              <button
                                  type="button"
                                  role="menuitem"
                                  disabled={deletingCuid === menuOpenCuid}
                                  className="block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
                                  onClick={() => {
                                      const rate = rates.find((r) => r.cuid === menuOpenCuid)
                                      if (rate != null) void handleDeleteRate(rate)
                                  }}
                              >
                                  Delete rate
                              </button>
                          </div>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    )
}

export default TariffRatesTable
